"use client";

import React, { useState, useEffect } from "react";
import { Info } from "lucide-react";

interface PromoTickerProps {
  bgColor?: string;
  textColor?: string;
  height?: string;
}

interface AppliedPromo {
  name: string;
  type: "price" | "percentage";
  value: number;
  promoOn: "vehicle" | "options" | "roadbook" | "total" | "insurance";
  message: string;
}

const PromoTicker: React.FC<PromoTickerProps> = ({
  bgColor = "bg-primary",
  textColor = "text-white",
  height = "h-8",
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [animationKey, setAnimationKey] = useState<number>(0);
  const [paused, setPaused] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  // Un seul code promo maintenant
  const promoCodes: AppliedPromo[] = [
    {
      name: "OFFRE DE LANCEMENT",
      type: "price",
      value: 10,
      promoOn: "vehicle",
      message: "Réduction de 10€/j sur la location d'un 4x4 à partir de 3 jours jusqu'au 31 juillet",
    },
  ];

  // Vérifie s'il faut faire l'animation (plusieurs codes)
  const shouldAnimate = promoCodes.length > 1;

  const nextMessage = () => {
    setCurrentIndex((prev) => (prev + 1) % promoCodes.length);
    setAnimationKey((prev) => prev + 1); // Redémarre l'animation
  };

  // Lance le timer de 5 secondes uniquement si l'animation n'est pas en pause ET s'il y a plusieurs codes
  useEffect(() => {
    if (!paused && shouldAnimate) {
      const timer = setTimeout(() => {
        nextMessage();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, paused, shouldAnimate]);

  // Copie le code promo (name) lorsqu'on clique et que l'animation est en pause
  const handleCopy = () => {
    if (paused || !shouldAnimate) {
      navigator.clipboard
        .writeText(promoCodes[currentIndex].name)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error("Erreur lors de la copie", err);
        });
    }
  };

  return (
    <div
      className={`fixed top-0 left-0 right-0 ${height} z-[9999] overflow-hidden ${bgColor} ${paused ? "paused" : ""
        }`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      onClick={handleCopy}
      style={{ cursor: paused || !shouldAnimate ? "pointer" : "default" }}
    >
      <div
        key={shouldAnimate ? animationKey : 0}
        className={`relative w-full h-full flex items-center justify-center ${shouldAnimate ? 'fade-animation' : ''}`}
      >
        <span className="promo-message text-gray-300 font-normal px-2 hidden md:block">
          {promoCodes[currentIndex].message} :
        </span>
        <span className={`promo-name ${textColor} font-bold px-2`}>
          {promoCodes[currentIndex].name}
        </span>
        <span title={promoCodes[currentIndex].message}>
          <Info size={16} className="block md:hidden text-white mb-1 ml-2" />
        </span>
      </div>
      {copied && <div className="copy-message">Copié dans le presse-papier !</div>}
      <style jsx>{`
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: scale(0.95);
          }
          4% {
            opacity: 1;
            transform: scale(1);
          }
          90% {
            opacity: 1;
            transform: scale(1);
          }
          100% {
            opacity: 0;
            transform: scale(0.95);
          }
        }
        .fade-animation {
          animation: fadeInOut 5s linear forwards;
        }
        @keyframes pulse {
          0%,
          40% {
            transform: scale(1);
          }
          43% {
            transform: scale(1.1);
          }
          46% {
            transform: scale(1);
          }
          49% {
            transform: scale(1.1);
          }
          52% {
            transform: scale(1);
          }
          55% {
            transform: scale(1.1);
          }
          58%,
          100% {
            transform: scale(1);
          }
        }
        .pulse-animation {
          animation: pulse 4s linear forwards;
        }
        .paused .fade-animation,
        .paused .pulse-animation {
          animation-play-state: paused;
        }
        .copy-message {
          position: absolute;
          bottom: 10px;
          right: 10px;
          background: rgba(0, 0, 0, 0.6);
          color: #fff;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.8rem;
        }
      `}</style>
    </div>
  );
};

export default PromoTicker;